<script setup>
import { computed } from 'vue';

import { useTheme } from '../composables/useTheme';

/**
 * Determinate linear progress: how far a known amount of work has got.
 *
 * @status recommended
 * @group status-feedback
 * @useWhen A long task reports how far it has got, such as installing a package
 * @avoidWhen How long it will take is unknown — use HrLoadingSpinner
 * @avoidWhen A file upload in a row or toolbar — use HrUploadProgressIndicator
 * @related HrLoadingSpinner
 * @related HrUploadProgressIndicator
 * @a11y Quasar renders it as a progressbar carrying its value, so assistive technology announces how far
 *   the work has got. Name it with `aria-label` after the work it tracks.
 */
defineOptions({ name: 'HrLinearProgress' });

const props = defineProps({
  /**
   * How far the work has got, from 0 to 1.
   * @example 0.4
   */
  value: {
    type: Number,
    required: true,
  },
  /** The work failed. Drawn in the error color, so the bar does not read as finished. */
  error: {
    type: Boolean,
    default: false,
  },
  /**
   * Legacy props forwarded to q-linear-progress when the legacy theme is active. Ignored under the
   * design-system theme.
   * @deprecated Remove once the legacy theme is removed.
   */
  legacyProps: {
    type: Object,
    default: () => ({}),
  },
});

const { hasTheme } = useTheme();

const effectiveProps = computed(() => (hasTheme.value
  ? {}
  : { color: props.error ? 'negative' : 'positive', rounded: true, size: '8px', ...props.legacyProps }));
</script>

<template>
  <q-linear-progress
    v-bind="effectiveProps"
    :value="value"
    :class="hasTheme ? ['hr-linear-progress', { 'hr-linear-progress--error': error }] : undefined"
  />
</template>

<style lang="scss" scoped>

/* Only applies to light/dark themes */

.hr-linear-progress {
  height: 4px;
  border-radius: 2px;
  color: var(--app-primary);
}

.hr-linear-progress :deep(.q-linear-progress__track) {
  background: var(--app-secondary-container);
  opacity: 1;
}

.hr-linear-progress--error {
  color: var(--app-error);
}

.hr-linear-progress--error :deep(.q-linear-progress__track) {
  background: var(--app-error-container);
}

</style>
